import React, { useState, useContext } from 'react';
import { Table, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import NotificationContext from './NotificationContext';

const EmailConfigTable = ({ onEditConfig }) => {
    const { notificationConfig, updateNotificationConfig, loading, canEditConfiguration, selectedRule } = useContext(NotificationContext);
    
    const [deleteModal, setDeleteModal] = useState({ show: false, index: null });
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [expandedEmails, setExpandedEmails] = useState({});

    const allConfigs = notificationConfig?.email_cfg || [];
    const emailConfigs = allConfigs.filter(config => !config.grps.includes('DEFAULT'));
    const defaultConfig = allConfigs.find(config => config.grps.includes('DEFAULT')) || { grps: ['DEFAULT'], emails: { to: [], cc: [] } };
    const defaultIndex = allConfigs.findIndex(config => config.grps.includes('DEFAULT'));

    const getGroupTypeLabel = () => 'Groups';

    const handleEdit = (index) => {
        const config = emailConfigs[index];
        const originalIndex = allConfigs.findIndex(c => c === config);
        onEditConfig(config, originalIndex);
    };

    const handleEditDefault = () => {
        if (defaultIndex !== -1) {
            onEditConfig(defaultConfig, defaultIndex);
        } else {
            onEditConfig(defaultConfig, -2); 
        }
    };

    const handleDelete = async (index) => {
        const config = emailConfigs[index];
        const originalIndex = allConfigs.findIndex(c => c === config);
        const updatedEmailConfigs = allConfigs.filter((_, i) => i !== originalIndex);
        const updatedConfig = {
            ...notificationConfig,
            email_cfg: updatedEmailConfigs 
        };
        const success = await updateNotificationConfig(updatedConfig);
        if (success) {
            setDeleteModal({ show: false, index: null });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
    };

    const renderEmailDisplay = (emails, configIndex, type) => {
        if (!emails) return null;

        const raw = Array.isArray(emails) ? emails : [emails];
        const emailArray = raw.flatMap(e =>
            typeof e === 'string' ? e.split(',').map(s => s.trim()).filter(Boolean) : [e]
        );
        if (emailArray.length === 0) return null;
        
        const expandKey = `${configIndex}-${type}`;
        const isExpanded = expandedEmails[expandKey];
        const displayEmails = isExpanded ? emailArray : emailArray.slice(0, 2);
        const remainingCount = emailArray.length - 2;

        return (
            <div className="d-flex flex-wrap gap-1">
                {displayEmails.map((email, index) => (
                    <div
                        key={index}
                        className="px-2 py-1"
                        style={{ 
                            backgroundColor: '#6c757d', 
                            color: 'white', 
                            borderRadius: '8px', 
                            fontSize: '0.65rem', 
                            fontWeight: '500',
                            padding: '1px 5px'
                        }}>
                        {email}
                    </div>
                ))}
                {!isExpanded && remainingCount > 0 && (
                    <div
                        className="px-2 py-1"
                        style={{ 
                            backgroundColor: '#e9ecef', 
                            color: '#6c757d', 
                            borderRadius: '12px', 
                            fontSize: '0.75rem', 
                            fontWeight: '500', 
                            cursor: 'pointer'
                        }}
                        onClick={() => setExpandedEmails({...expandedEmails, [expandKey]: true})}>
                            +{remainingCount} more
                    </div>
                )}
                {isExpanded && emailArray.length > 2 && (
                    <div
                        className="px-2 py-1"
                        style={{ 
                            backgroundColor: '#e9ecef',  
                            color: '#6c757d', 
                            borderRadius: '12px', 
                            fontSize: '0.75rem', 
                            fontWeight: '500', 
                            cursor: 'pointer'
                        }}
                        onClick={() => setExpandedEmails({...expandedEmails, [expandKey]: false})}>
                            show less
                    </div>
                )}
            </div>
        );
    };

    if (!canEditConfiguration()) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#fff3cd', borderRadius: '5px', border: '1px solid #ffeaa7', color: '#856404' }}>
                <div className="mb-2">
                    <i className="fas fa-lock" style={{fontSize: '48px', color: '#856404'}}></i>
                </div>
                <h6 className="mb-2">Configuration Not Editable</h6>
                <p className="mb-0">This configuration cannot be edited due to system restrictions.</p>
            </div>
        );
    }

    return (
        <>
            {saveSuccess && (
                <Alert color="success" className="mb-3">
                    <i className="fas fa-check-circle me-2"></i>
                    Configuration saved successfully!
                </Alert>
            )}

            <div className="react-table">
                <Table bordered  responsive style={{
                    fontFamily: 'Inter, Poppins, sans-serif', fontSize: '13px', lineHeight: '19.5px' 
                }}>
                    <thead className="table-light table-nowrap"> 
                        <tr>
                            <th style={{ width: '25%' }}>
                                <div className="mb-2" style={{ fontWeight: '600' }}>
                                    {getGroupTypeLabel()}
                                </div>
                            </th>
                            <th style={{ width: '32%' }}>
                                <div className="mb-2" style={{ fontWeight: '600' }}>
                                    TO
                                </div>
                            </th>
                            <th style={{ width: '32%' }}>
                                <div className="mb-2" style={{ fontWeight: '600' }}>
                                    CC
                                </div>
                            </th>
                            <th className="text-center" style={{ width: 'auto' }}>
                                <div className="mb-2" style={{ fontWeight: '600' }}>
                                    Actions
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {emailConfigs.map((config, index) => (
                            <tr key={index}>
                                <td>
                                    <div className="d-flex flex-wrap gap-1">
                                        {config.grps.map((group, i) => (
                                            <Badge key={i} color="primary" className="fw-bold" style={{ fontSize: '0.7rem', padding: '2px 8px', fontWeight: '500' }}>
                                                {group}
                                            </Badge>
                                        ))}
                                    </div>
                                </td>
                                <td>{renderEmailDisplay(config.emails.to, index, 'to')}</td>
                                <td>{renderEmailDisplay(config.emails.cc, index, 'cc')}</td>
                                <td className="text-center">
                                    <div className="d-flex justify-content-center gap-2">
                                        <Button size="sm" color="outline-primary" onClick={() => handleEdit(index)} disabled={loading} title="Edit">
                                            <i className="fas fa-edit"></i>
                                        </Button>
                                        <Button size="sm" color="outline-danger" onClick={() => setDeleteModal({ show: true, index })} disabled={loading} title="Delete">
                                            <i className="fas fa-trash"></i>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {/* DEFAULT row — always last */}
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <td>
                                <Badge color="secondary" className="fw-bold" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>DEFAULT</Badge>
                            </td>
                            <td>{renderEmailDisplay(defaultConfig.emails.to, 'default', 'to')}</td>
                            <td>{renderEmailDisplay(defaultConfig.emails.cc, 'default', 'cc')}</td>
                            <td className="text-center">
                                <Button size="sm" color="outline-primary" onClick={handleEditDefault} disabled={loading} title="Edit DEFAULT">
                                    <i className="fas fa-edit"></i>
                                </Button>
                            </td>
                        </tr>
                    </tbody>
                </Table> 
            </div>

            <Modal isOpen={deleteModal.show} toggle={() => setDeleteModal({ show: false, index: null })}>
                <ModalHeader toggle={() => setDeleteModal({ show: false, index: null })} className="bg-danger text-white">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Confirm Deletion
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
                    <h5 className="mb-2">Are you sure?</h5>
                    <p className="text-muted mb-0">
                        This will permanently delete this email configuration.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={() => handleDelete(deleteModal.index)} disabled={loading}>
                        <i className="fas fa-trash me-1"></i>
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Button color="secondary" onClick={() => setDeleteModal({ show: false, index: null })}>
                        <i className="fas fa-times me-1"></i>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default EmailConfigTable;